import Card from '@/components/card';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import Select from '@/components/form/select';
import Text from '@/components/text';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import {
  APPLICATION_TYPE_LABELS,
  MODEL_FAMILIES,
  MODEL_FAMILY_LABELS,
  ModelFamily,
} from '@/lib/models/policy-model/reroute';
import { PrivateModelModel } from '@/lib/models/private-model/private-model.model';
import { Box, Grid, Stack, Theme } from '@mui/material';
import { FC, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import Tooltip from '@/components/tooltip';
import { InputControlValue } from '../../input-control/helpers';
import useFeature from '@/hooks/use-feature.hook';
import Chip from '@/components/chip';
import { UiOption } from '@/lib/helpers';
import InlineToast from '@/components/toasts/inline';
import PageLink from '@/components/page-link';
import PrivateModelLink from '@/components/private-model-link';
import { capitalize } from 'lodash';
import IntercomButton from '@/components/intercom/intercom-button';
import Icon from '@/components/icon';
import useDrawer from '@/hooks/use-drawer.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

interface Props {
  formHandle: FormHandle<PolicyModelV1>;
  readonly: boolean;
  privateModels: PrivateModelModel[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  rerouteItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: 8,
      paddingBottom: 8,
    },
  },
  select: {
    height: 60,
  },
}));

const NO_COMPATIBLE_MODELS = 'no-compatible-models';

const getFamilyPrivateModelOptions = (
  privateModels: PrivateModelModel[],
  family: ModelFamily
): UiOption[] => {
  const { recommendedModelId, modelFamilyOptions } = MODEL_FAMILY_LABELS[family];
  const normalizedRecommendedModelId = recommendedModelId.toLowerCase();

  return privateModels
    .filter(({ modelId }) =>
      modelFamilyOptions.some((option) => modelId.toLowerCase().includes(option))
    )
    .map((privateModel) => {
      const { id, modelId } = privateModel;
      const normalizedModelId = modelId.toLowerCase();
      const isRecommended = normalizedModelId.includes(normalizedRecommendedModelId);

      return {
        label: (
          <Stack direction="row" alignItems="center" gap={1} width="100%">
            <Box flexGrow={1} flexShrink={1} minWidth={0}>
              <PrivateModelLink model={privateModel} showProviderModelId action="none" />
            </Box>
            {isRecommended && (
              <Box flexShrink={0}>
                <Chip label="Recommended" size="small" />
              </Box>
            )}
          </Stack>
        ),
        value: id,
      };
    });
};

const getPrivateModelOptionsByFamily = (
  privateModels: PrivateModelModel[]
): Record<ModelFamily, UiOption[]> =>
  MODEL_FAMILIES.reduce(
    (acc, family) => {
      acc[family] = getFamilyPrivateModelOptions(privateModels, family);
      return acc;
    },
    {} as Record<ModelFamily, UiOption[]>
  );

const RerouteControls: FC<Props> = ({ formHandle, readonly, privateModels }) => {
  const { canViewReroute } = useFeature();
  const { openDrawer } = useDrawer();

  const styles = useStyles();
  const { values, setFieldValue } = formHandle;
  const { enabled: isEnabled, modelFamilyReroutes } = values.reroute || {};
  const entries = useMemo(() => modelFamilyReroutes ?? {}, [modelFamilyReroutes]);
  // In future iteration, we will support application type as a form section prop
  const applicationType = 'claude-code';
  const applicationName = APPLICATION_TYPE_LABELS[applicationType];

  const privateModelOptionsByFamily = useMemo(
    () => getPrivateModelOptionsByFamily(privateModels || []),
    [privateModels]
  );

  const handleEnabledChange = (checked: InputControlValue) => {
    const compValue = checked && hasAllFamilySelections;
    setFieldValue('reroute.enabled', compValue);
  };

  const handleModelChange = (family: ModelFamily, privateModelId: string) => {
    const compModelId = privateModelId === NO_COMPATIBLE_MODELS ? '' : privateModelId;
    setFieldValue('reroute.modelFamilyReroutes', {
      ...entries,
      [family]: {
        privateModelId: compModelId,
      },
    });
  };

  const hasPrivateModels = !!privateModels?.length;
  const hasAllFamilySelections = useMemo(
    () => MODEL_FAMILIES.every((family) => !!entries[family]?.privateModelId),
    [entries]
  );
  const rerouteToggleDisabled = !isEnabled && !hasAllFamilySelections;

  // Build the sorted entries matching MODEL_FAMILIES order for display
  const sortedEntries = useMemo(
    () =>
      MODEL_FAMILIES.map((family) => ({
        family,
        privateModelId: entries[family]?.privateModelId || '',
      })),
    [entries]
  );

  const hasSonnetOrOpus = useMemo(
    () =>
      privateModels?.some(({ modelId }) => {
        const normalizedModelId = modelId.toLowerCase();
        return normalizedModelId.includes('sonnet') || normalizedModelId.includes('opus');
      }),
    [privateModels]
  );

  const selectedModelHasError = useMemo(() => {
    const selectedModelIds = Object.values(entries).map(({ privateModelId }) => privateModelId);
    return selectedModelIds.some(
      (id) => privateModels.find((model) => model.id === id)?.status === 'error'
    );
  }, [privateModels, entries]);

  const onInfoClick = (privateModelId: string) => {
    openDrawer('private-model', { id: privateModelId });
  };

  if (!canViewReroute) {
    return null;
  }

  return (
    <FormSection title={`${applicationName} Model Router`} scrollId="reroute-controls">
      <FormSectionSubtitle>
        Using the Model Gateway, you can route Claude Code requests to a configured private model.
        Map each Claude model to a private model of similar or better capability to ensure optimal
        performance and avoid unexpected behavior.
      </FormSectionSubtitle>
      <Grid item xs={12} mb={2} gap={1}>
        <Box>
          <IntercomButton
            intercomArticle="model-gateway"
            flatButton
            label="Model Gateway Configuration Guides"
          />
        </Box>
      </Grid>

      {!hasSonnetOrOpus && (
        <InlineToast
          level="warning"
          message={
            <Text>
              No Sonnet or Opus private models found. Please add at least one Sonnet or Opus private
              model to ensure Claude Code performance is not degraded. Go to {` `}
              <PageLink routeKey="private-models" scrollId="private-model" />
            </Text>
          }
        />
      )}

      {selectedModelHasError && (
        <InlineToast
          level="error"
          message="One or more selected private models are in an error state. Claude Code will silently fail to route requests to these models."
        />
      )}

      <Grid item xs={5} mb={2}>
        <Tooltip
          title={
            rerouteToggleDisabled ? 'To enable, configure a private model for each option' : ''
          }
          placement="bottom-start"
        >
          <Box width={300}>
            <InputControl
              label="Enabled"
              name="reroute.enabled"
              type="switch"
              formHandle={formHandle}
              readonly={readonly}
              disabled={rerouteToggleDisabled}
              onChange={handleEnabledChange}
            />
          </Box>
        </Tooltip>
      </Grid>

      {hasPrivateModels && (
        <Card>
          {sortedEntries.map(({ family, privateModelId }) => {
            const hasPrivateModel = !!privateModelId;
            const { label: familyLabel, modelFamilyOptions } = MODEL_FAMILY_LABELS[family];
            const familyOptions = privateModelOptionsByFamily[family];
            const hasCompatibleOptions = !!familyOptions.length;
            const showInfoClick = hasPrivateModel && hasCompatibleOptions;
            const compatibleFamilies = modelFamilyOptions
              .map((option) => capitalize(option))
              .join(', ');
            return (
              <Stack
                key={family}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={styles.rerouteItem}
              >
                <Box width={150} minWidth={150}>
                  <Text>{familyLabel}</Text>
                </Box>
                <Stack direction="row" flexGrow={1} maxWidth={400} gap={1} alignItems="center">
                  <Select
                    name={`reroute.modelFamilyReroutes.${family}.privateModelId`}
                    options={
                      hasCompatibleOptions
                        ? familyOptions
                        : [
                            {
                              label: (
                                <Box>
                                  <Text size="small">No compatible private models available</Text>
                                  <Text size="small" color="grey">
                                    Acceptable private models: {compatibleFamilies}
                                  </Text>
                                </Box>
                              ),
                              value: NO_COMPATIBLE_MODELS,
                              disabled: true,
                            },
                          ]
                    }
                    readonly={readonly}
                    value={hasCompatibleOptions ? privateModelId : NO_COMPATIBLE_MODELS}
                    onChange={(value: string) => handleModelChange(family, value)}
                    size="small"
                    className={styles.select}
                  />
                  {showInfoClick && (
                    <Tooltip title="View private model details">
                      <Icon
                        name="info-circle"
                        onClick={() => onInfoClick(privateModelId)}
                        size="small"
                        color="grey"
                      />
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Card>
      )}
    </FormSection>
  );
};

export default RerouteControls;
